/**
 * @file DataHealth.tsx
 * @description View 2 — Data Health & Reconciliation Transparency Log.
 *
 * Shows the operations lead EXACTLY how every conflict was detected and
 * resolved. Columns: Issue | Patient | Conflicting Sources | Resolution.
 *
 * Also includes a conflict-type breakdown chart.
 */

import { useState } from "react";
import { ShieldAlert, Search, Filter } from "lucide-react";
import { AuditLogEntry, ConflictType, SystemMetrics } from "../../types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface DataHealthProps {
  entries: AuditLogEntry[];
  metrics: SystemMetrics | null;
  loading: boolean;
}

// ─── Conflict config ─────────────────────────────────────────────────────────
const CONFLICT_CONFIG: Record<
  ConflictType,
  { label: string; color: string; bg: string; border: string; rule: string }
> = {
  "Status Mismatch": {
    label: "Status Mismatch",
    color: "#ef4444",
    bg: "bg-red-500/15",
    border: "border-red-500/30",
    rule: "Rule 1",
  },
  "Location Desync": {
    label: "Location Desync",
    color: "#f59e0b",
    bg: "bg-amber-500/15",
    border: "border-amber-500/30",
    rule: "Rule 2",
  },
  "Orphaned Lab Record": {
    label: "Orphaned Lab",
    color: "#8b5cf6",
    bg: "bg-purple-500/15",
    border: "border-purple-500/30",
    rule: "Rule 3",
  },
  "Post-Discharge Anomaly": {
    label: "Post-Discharge",
    color: "#f97316",
    bg: "bg-orange-500/15",
    border: "border-orange-500/30",
    rule: "Rule 4",
  },
  Clean: {
    label: "Clean",
    color: "#22c55e",
    bg: "bg-green-500/15",
    border: "border-green-500/30",
    rule: "—",
  },
};

// ─── Source badge ─────────────────────────────────────────────────────────────
function SourceBadge({ source }: { source: "HIS" | "Lab" | "BedSheet" }) {
  const cfg: Record<string, string> = {
    HIS: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Lab: "bg-teal-500/15 text-teal-400 border-teal-500/30",
    BedSheet: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${cfg[source]}`}>
      {source}
    </span>
  );
}

// ─── Expandable row ───────────────────────────────────────────────────────────
function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = CONFLICT_CONFIG[entry.conflict_type] ?? CONFLICT_CONFIG.Clean;
  const formattedTime = new Date(entry.timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      <tr
        className="border-b border-white/5 hover:bg-white/3 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Issue Detected */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${cfg.bg} ${cfg.border}`}
              style={{ color: cfg.color }}
            >
              <span className="text-gray-500 text-xs font-normal">{cfg.rule}</span>
              {cfg.label}
            </span>
          </div>
          <div className="text-gray-600 text-xs mt-1 font-mono">{formattedTime}</div>
        </td>

        {/* Patient */}
        <td className="px-6 py-4">
          <div className="text-white text-sm font-medium">{entry.patient_name}</div>
          <div className="text-gray-500 text-xs font-mono mt-0.5">{entry.patient_id}</div>
        </td>

        {/* Conflicting Sources */}
        <td className="px-6 py-4">
          <div className="flex flex-wrap gap-1.5">
            {entry.sources_involved.map((s) => (
              <SourceBadge key={s} source={s} />
            ))}
          </div>
        </td>

        {/* Resolution (truncated) */}
        <td className="px-6 py-4">
          <p className="text-gray-300 text-sm leading-relaxed line-clamp-2">
            {entry.resolution_applied}
          </p>
          <button className="text-teal-400 text-xs mt-1 hover:underline">
            {expanded ? "▲ Collapse" : "▼ Show detail"}
          </button>
        </td>
      </tr>

      {/* Expanded detail row */}
      {expanded && (
        <tr className="bg-white/2">
          <td colSpan={4} className="px-6 pb-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className="rounded-xl p-4 border border-white/8"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                  Conflict Detail
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {entry.conflict_detail}
                </p>
              </div>
              <div
                className="rounded-xl p-4 border border-teal-500/20"
                style={{ background: "rgba(20, 184, 166, 0.04)" }}
              >
                <div className="text-xs text-teal-500 uppercase tracking-wider mb-2">
                  Resolution Applied
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {entry.resolution_applied}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-xs text-gray-600 font-mono">
                Run ID: {entry.reconciliation_run_id}
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button 
                  onClick={() => alert(`Conflict escalated to clinical lead for patient ${entry.patient_id}`)}
                  className="px-4 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors flex-1 sm:flex-none whitespace-nowrap"
                >
                  Escalate Conflict
                </button>
                <button 
                  onClick={() => {
                    alert(`Conflict marked as acknowledged for patient ${entry.patient_id}`);
                    setExpanded(false);
                  }}
                  className="px-4 py-1.5 rounded-lg bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 text-xs font-medium transition-colors flex-1 sm:flex-none whitespace-nowrap"
                >
                  Acknowledge Resolution
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function DataHealth({ entries, metrics, loading }: DataHealthProps) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<ConflictType | "All">("All");

  const filtered = entries.filter((e) => {
    const matchType = filterType === "All" || e.conflict_type === filterType;
    const matchSearch =
      !search ||
      e.patient_name.toLowerCase().includes(search.toLowerCase()) ||
      e.patient_id.toLowerCase().includes(search.toLowerCase()) ||
      e.conflict_type.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  // Bar chart data
  const chartData = [
    { name: "Status\nMismatch", value: metrics?.status_mismatches ?? 0, color: "#ef4444" },
    { name: "Location\nDesync", value: metrics?.location_desyncs ?? 0, color: "#f59e0b" },
    { name: "Orphaned\nLab", value: metrics?.orphaned_lab_records ?? 0, color: "#8b5cf6" },
    { name: "Post-\nDischarge", value: metrics?.post_discharge_anomalies ?? 0, color: "#f97316" },
  ];

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── Summary Stats Row ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(
          [
            ["Status Mismatch", metrics?.status_mismatches, "#ef4444", "Rule 1"],
            ["Location Desync", metrics?.location_desyncs, "#f59e0b", "Rule 2"],
            ["Orphaned Lab", metrics?.orphaned_lab_records, "#8b5cf6", "Rule 3"],
            ["Post-Discharge", metrics?.post_discharge_anomalies, "#f97316", "Rule 4"],
          ] as const
        ).map(([label, val, color, rule]) => (
          <div
            key={label}
            className="rounded-2xl border border-white/8 p-5"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
            }}
          >
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{rule}</div>
            <div className="text-2xl font-bold mb-0.5" style={{ color }}>
              {loading ? "–" : (val ?? 0)}
            </div>
            <div className="text-gray-400 text-xs">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Breakdown Chart + Rule Explanations ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Bar chart */}
        <div
          className="rounded-2xl border border-white/8 p-6"
          style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
          }}
        >
          <h2 className="text-white font-semibold text-base mb-5">Conflict Breakdown</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barSize={40}>
              <XAxis
                dataKey="name"
                tick={{ fill: "#6b7280", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#6b7280", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.03)" }}
                contentStyle={{
                  background: "rgba(7,20,40,0.98)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "12px",
                  fontSize: 12,
                  color: "#fff",
                }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Rule explanations */}
        <div
          className="rounded-2xl border border-white/8 p-6"
          style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
          }}
        >
          <h2 className="text-white font-semibold text-base mb-4">Reconciliation Rules</h2>
          <div className="space-y-3">
            {[
              { rule: "Rule 1", name: "Status Mismatch", color: "#ef4444", desc: "HIS=Discharged but Bed=Occupied → 'Bed Requires Cleaning/Check'" },
              { rule: "Rule 2", name: "Location Desync", color: "#f59e0b", desc: "HIS bed ≠ Bed Sheet bed → Trust Bed Sheet, log warning" },
              { rule: "Rule 3", name: "Orphaned Lab Record", color: "#8b5cf6", desc: "Lab patient_id not in HIS → Create temporary profile, never discard" },
              { rule: "Rule 4", name: "Post-Discharge Anomaly", color: "#f97316", desc: "Lab result_datetime > HIS discharge_datetime → Flag for clinical review" },
            ].map(({ rule, name, color, desc }) => (
              <div
                key={rule}
                className="flex gap-3 p-3 rounded-xl"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div
                  className="text-xs font-mono font-bold px-2 py-1 rounded-lg flex-shrink-0 h-fit"
                  style={{ background: `${color}20`, color }}
                >
                  {rule}
                </div>
                <div>
                  <div className="text-white text-sm font-medium">{name}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Audit Log Table ── */}
      <div
        className="rounded-2xl border border-white/8 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
        }}
      >
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-white/5 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-teal-400" />
            <h2 className="text-white font-semibold text-base">
              Conflict Resolution Log
            </h2>
            <span className="text-gray-600 text-xs">({filtered.length} entries)</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
              <input
                id="audit-search"
                type="text"
                placeholder="Search patient, ID, type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-xs rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600
                           focus:outline-none focus:ring-2 focus:ring-teal-500/40 w-56"
              />
            </div>

            {/* Filter */}
            <div className="relative flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-gray-500" />
              <select
                id="audit-filter"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as ConflictType | "All")}
                className="text-xs py-2 px-3 rounded-xl bg-white/5 border border-white/10 text-white
                           focus:outline-none focus:ring-2 focus:ring-teal-500/40 appearance-none pr-6"
              >
                <option value="All">All Types</option>
                <option value="Status Mismatch">Status Mismatch</option>
                <option value="Location Desync">Location Desync</option>
                <option value="Orphaned Lab Record">Orphaned Lab</option>
                <option value="Post-Discharge Anomaly">Post-Discharge</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {["Issue Detected", "Patient", "Conflicting Sources", "How System Resolved It"].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-white/5">
                    {Array.from({ length: 4 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 rounded bg-white/5 animate-pulse w-32" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-gray-600">
                    No conflicts match your filter criteria.
                  </td>
                </tr>
              ) : (
                filtered.map((entry) => (
                  <AuditRow key={entry.id ?? entry.timestamp + entry.patient_id} entry={entry} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
